/**
 * Desktop notifications for incoming calls/chats and chat messages.
 *
 * Uses the browser Notification API so agents are alerted even when ESP is
 * not the focused window (working in another app or browser tab). The window
 * focus gate means a notification is only shown when ESP is in the background;
 * when the agent is already looking at ESP the in-app modal / chat chime covers
 * it.
 *
 * Selecting a notification focuses the existing ESP window. It never opens a
 * new page, tab, or the AWS/Connect workspace.
 */

import type { CallMessage } from '@/types/call';
import type { AlertEvent } from '@/types/alerts';
import i18n from '@/i18n';

export interface DesktopNotificationOptions {
  title: string;
  body: string;
  tag?: string;
  onClick?: () => void;
}

/** True when the Notification API is present and permission was granted. */
function canNotify(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted'
  );
}

/** True when ESP is not the focused window (agent in another app/tab). */
function isWindowInBackground(): boolean {
  return typeof document === 'undefined' || !document.hasFocus();
}

export type DesktopNotificationPermission =
  | NotificationPermission
  | 'unsupported';

/** Current browser notification permission, or 'unsupported' if unavailable. */
export function getDesktopNotificationPermission(): DesktopNotificationPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Request notification permission. Call from a user gesture (button click) so
 * the browser reliably shows its permission prompt. Resolves to the resulting
 * permission state.
 */
export async function requestDesktopNotificationPermission(): Promise<DesktopNotificationPermission> {
  const permission = getDesktopNotificationPermission();
  if (permission !== 'default') return permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return getDesktopNotificationPermission();
  }
}

function showDesktopNotification(opts: DesktopNotificationOptions): void {
  if (!canNotify()) return;
  if (!isWindowInBackground()) return;

  try {
    const notification = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      opts.onClick?.();
    };
  } catch {
    // Notification may fail (e.g. unsupported platform) — never throw.
  }
}

/**
 * Incoming call/chat notification. Re-fired with the same `tag` (contact id)
 * when the phone number arrives, so the OS replaces the earlier toast rather
 * than stacking duplicates.
 */
export function notifyIncomingContact(
  contactId: string,
  isChat: boolean,
  phoneNumber?: string | null
): void {
  const title = i18n.t(
    isChat
      ? 'components.desktopNotification.incomingChat'
      : 'components.desktopNotification.incomingCall'
  );
  const body =
    phoneNumber ??
    i18n.t(
      isChat
        ? 'components.desktopNotification.newChat'
        : 'components.desktopNotification.newCall'
    );
  showDesktopNotification({ title, body, tag: contactId });
}

/** Chat message notification. Client messages only; silent for agent/bot. */
export function notifyChatMessage(
  message: Pick<CallMessage, 'talker' | 'message'>,
  contactId: string
): void {
  if (message.talker !== 'client') return;
  const body = (message.message ?? '').trim();
  if (!body) return;
  showDesktopNotification({
    title: i18n.t('components.desktopNotification.newMessage'),
    body,
    tag: `chat-${contactId}`,
  });
}

/** Custom DOM event fired when a missed-contact notification is clicked. */
export const ALERTS_NAVIGATION_EVENT = 'spog:navigate-to-alerts';

/**
 * Missed call/chat team alert notification. Shown when ESP is in the background.
 * Body = agent name · queue name; title distinguishes call vs chat.
 * Clicking dispatches ALERTS_NAVIGATION_EVENT so the SPA navigates to Alerts.
 */
export function notifyMissedContactAlert(alert: AlertEvent): void {
  const isChat = alert.type === 'MISSED_CHAT';
  const title = i18n.t(
    isChat
      ? 'components.desktopNotification.missedChat'
      : 'components.desktopNotification.missedCall'
  );
  const body =
    [alert.agentName, alert.queueName].filter(Boolean).join(' · ') || title;
  showDesktopNotification({
    title,
    body,
    tag: `alert-${alert.id}`,
    onClick: () => window.dispatchEvent(new Event(ALERTS_NAVIGATION_EVENT)),
  });
}
